import { ListingCategoryConst, PrefTypeConst } from '../constants/ListingCategoryConst';

/**
 * 絞り込み条件の「未設定」判定と、出品区分×県区分の選択値の組み立て。
 *
 * 🔒 なぜ専用の関数が要るか:
 *   入札会は県内・県外の区別を持たず、県区分を **0** で表す（サーバ側も PrefectureType=0 で一貫）。
 *   ところが JavaScript では 0 が falsy のため、`if (!prefectureType)` や `a && b` といった
 *   素朴な書き方は **0 を「未設定」と誤判定**する。判定をこの1箇所に集約して誤判定を防ぐ。
 *
 * 設計: docs/sdd/phases/p2-design/design-common-foundation.md §3（真理値表）
 */

/** 絞り込み条件の値（URL クエリ・Context・localStorage を経由するため文字列空も取りうる） */
type FilterValue = number | '' | null | undefined;

/**
 * 「未設定」かどうかを判定する。
 *
 * 🔒 **0 は「設定済み」**（入札会の県区分）。NaN は未設定扱い
 *   （localStorage に `NaN` が保存されてしまう既存バグの受け皿＝G2/G3）。
 */
export const isUnsetFilter = (value: FilterValue): boolean => {
  if (value === '' || value == null) {
    return true; // '' / null / undefined
  }
  return Number.isNaN(value);
};

/**
 * 出品区分と県区分から、選択欄の value（`"2_1"` 形式）を組み立てる。
 * どちらかが未設定なら空文字を返す（選択なしの状態）。
 *
 * 🔒 入札会は `"3_0"` になる。`listingCategory && prefectureType` という書き方では
 *   県区分 0 が falsy のため空文字になってしまうので、必ず本関数を使う。
 */
export const toCombinedValue = (
  listingCategory: FilterValue,
  prefectureType: FilterValue,
): string => {
  if (isUnsetFilter(listingCategory) || isUnsetFilter(prefectureType)) {
    return '';
  }
  return `${listingCategory}_${prefectureType}`;
};

/**
 * 出品区分と県区分の組み合わせが正当かどうかを判定する（K3・D-0086・タスク7）。
 *
 * 🔒 サーバの G8（`BiddingOwnershipValidator.IsValidPrefectureFor`）と**同じ真理値表**。
 *   入札会(3)は県区分0のみ、通常入札(2)は県内(1)/県外(2)のみが正当。相対(1)は判定対象外（未取扱のため常に true）。
 *
 * ⚠ これは**サーバの G8 を代替しない**——あくまで localStorage の記憶（`getFilterPreference`）を
 *   読み戻す際の**表示側の防御**（読み出し時に妥当性を確かめる＝案①）。組み合わせの最終的な検証は
 *   引き続きサーバ側 G8 が担う。
 */
export const isValidCombination = (
  listingCategory: FilterValue,
  prefectureType: FilterValue,
): boolean => {
  if (isUnsetFilter(listingCategory) || isUnsetFilter(prefectureType)) {
    return false;
  }
  switch (listingCategory) {
    case ListingCategoryConst.SALE:
      return prefectureType === PrefTypeConst.NONE;
    case ListingCategoryConst.BIDDING:
      return prefectureType === PrefTypeConst.INTERNAL || prefectureType === PrefTypeConst.EXTERNAL;
    default: // 相対（PRIVATE）等は未取扱のため対象外
      return true;
  }
};
